'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import Logo from '@/components/ui/Logo'
import ErrorBoundary from '@/components/ErrorBoundary'
import PartnerBrandBadge from '@/components/partner/PartnerBrandBadge'
import useHardSignOut from '@/hooks/useHardSignOut'
import {
  ArrowLeftOnRectangleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  BeakerIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TagIcon,
  UserGroupIcon,
  BoltIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline'
import type { Portal } from '@/lib/subdomain'
import type { ComponentType, SVGProps } from 'react'

type NavItem = {
  label: string
  href: string
  icon?: ComponentType<SVGProps<SVGSVGElement>>
}

// Operator console tabs — moved here from src/app/admin/layout.tsx so the
// portal's navigation lives in its header chrome, not the page body.
const ADMIN_NAV: NavItem[] = [
  { label: 'Organizations', href: '/admin/organizations', icon: BuildingOfficeIcon },
  { label: 'Users', href: '/admin/users', icon: UsersIcon },
  { label: 'Projects', href: '/admin/analysis', icon: BeakerIcon },
  { label: 'Usage', href: '/admin/usage', icon: ChartBarIcon },
  { label: 'Plans', href: '/admin/plans', icon: CurrencyDollarIcon },
  { label: 'Topics', href: '/admin/topics', icon: TagIcon },
  { label: 'Partners', href: '/admin/partners', icon: UserGroupIcon },
  { label: 'Agents', href: '/admin/agents', icon: BoltIcon },
  { label: 'Assets', href: '/admin/assets', icon: DocumentArrowUpIcon },
]

/**
 * Best-match active detection: the active tab is the one whose href is the
 * LONGEST prefix of the current path (exact match or a child route). This is
 * required for index-style tabs — e.g. the partner "Organizations" tab at
 * `/partners` must NOT stay active on `/partners/team`.
 */
function activeHref(pathname: string | null, items: NavItem[]): string | null {
  if (!pathname) return null
  let best: string | null = null
  for (const { href } of items) {
    if (pathname === href || pathname.startsWith(href + '/')) {
      if (best === null || href.length > best.length) best = href
    }
  }
  return best
}

/**
 * Minimal chrome for the standalone portal hosts (admin.42a.ai /
 * partners.42a.ai). Deliberately does NOT render the customer app shell —
 * no Sidebar, no CommandPalette, no platform navigation — so an operator or
 * partner on a portal host has access to that portal ONLY, not the whole
 * platform.
 *
 * The header carries the portal's own tab navigation (operator console tabs /
 * partner portal tabs), the 42A logo, and sign out (via useHardSignOut per the
 * app-wide sign-out rules). The page body renders only the active tab's content.
 */
export default function PortalShell({
  portal,
  children,
}: {
  portal: Portal
  children: React.ReactNode
}) {
  const handleSignOut = useHardSignOut()
  const pathname = usePathname()
  // Partner tab labels are localized (partners.page.tabs.*); admin labels match
  // the previous hardcoded operator console. useTranslations is called
  // unconditionally (hook rules); the result is only used for the partner nav.
  const tPartner = useTranslations('partners.page')

  const label = portal === 'admin' ? 'Operator Console' : 'Partner Portal'
  const nav: NavItem[] =
    portal === 'admin'
      ? ADMIN_NAV
      : [
          { label: tPartner('tabs.organizations'), href: '/partners' },
          { label: tPartner('tabs.reports'), href: '/partners/reports' },
          { label: tPartner('tabs.tasks'), href: '/partners/tasks' },
          { label: tPartner('tabs.team'), href: '/partners/team' },
          { label: tPartner('tabs.codes'), href: '/partners/referrals' },
          { label: tPartner('tabs.branding'), href: '/partners/branding' },
        ]
  const active = activeHref(pathname, nav)

  return (
    <div className="flex h-full flex-col" style={{ background: 'var(--color-background)' }}>
      <header
        className="flex-shrink-0 border-b"
        style={{ background: 'var(--color-sidebar)', borderColor: 'var(--color-border)' }}
      >
        {/* Brand row */}
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <Logo width={94} height={35} />
            <span
              className="rounded-md px-2 py-0.5 text-xs font-medium"
              style={{ background: 'var(--color-surface-hover)', color: 'var(--color-text-secondary)' }}
            >
              {label}
            </span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors hover:bg-[var(--color-surface-hover)]"
            style={{ color: 'var(--color-text-primary)' }}
          >
            <ArrowLeftOnRectangleIcon className="h-4 w-4" />
            Sign out
          </button>
        </div>

        {/* Tab row — partner branding pinned to the right on the partner portal.
            pe-4 aligns the badge with the sign-out button's right edge (px-4). */}
        <div className="flex items-center justify-between gap-3 pe-4">
          <nav className="flex items-center gap-1 overflow-x-auto px-2">
            {nav.map((item) => {
              const isActive = item.href === active
              const Icon = item.icon
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center gap-2 whitespace-nowrap border-b-2 -mb-[1px] px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-[var(--color-lime)] text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-[var(--color-border-light)]'
                  }`}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  {item.label}
                </Link>
              )
            })}
          </nav>
          {portal === 'partners' && <PartnerBrandBadge className="shrink-0" />}
        </div>
      </header>
      <main className="flex-1 min-w-0 overflow-auto" style={{ background: 'var(--color-background)' }}>
        <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
      </main>
    </div>
  )
}
